import { hasGrant, hasGrantAnywhere } from '@growup/accounts-shared';

/**
 * Who is signed in, read from the access token. Only shapes the UI; every API
 * checks the token itself. Grants are `"permission"` (every arena) or
 * `"permission@<arenaId>"` (docs/contas/README.md).
 */
export interface StaffSession {
  accountId: string;
  login: string;
  grants: string[];
}

export function decodeSession(token: string): StaffSession | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const json = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))) as {
      sub?: unknown;
      login?: unknown;
      grants?: unknown;
    };
    if (typeof json.sub !== 'string') return null;
    return {
      accountId: json.sub,
      login: typeof json.login === 'string' ? json.login : '',
      grants: Array.isArray(json.grants)
        ? json.grants.filter((g): g is string => typeof g === 'string')
        : [],
    };
  } catch {
    return null;
  }
}

export const isAdmin = (s: StaffSession) => hasGrant(s.grants, 'arena.manage');

export const hasArenaAccess = (s: StaffSession) =>
  isAdmin(s) || hasGrantAnywhere(s.grants, 'arena.monitor');

export const canRegister = (s: StaffSession, arenaId: string) =>
  isAdmin(s) || hasGrant(s.grants, 'members.register_youth', arenaId);

export const canApproveMembers = (s: StaffSession) => hasGrant(s.grants, 'members.approve');

export const canViewMembers = (s: StaffSession) => hasGrant(s.grants, 'members.view');

export const canManageMembers = (s: StaffSession) => hasGrant(s.grants, 'members.manage');

export const canManageStaff = (s: StaffSession) => hasGrant(s.grants, 'staff.manage');

export const hasMembersAccess = (s: StaffSession) => canApproveMembers(s) || canViewMembers(s);

export const canManageStore = (s: StaffSession) => hasGrant(s.grants, 'store.manage');

export const canManageEvents = (s: StaffSession) => hasGrant(s.grants, 'events.manage');
/** Event budget, costs and invoices (Events Manager, General Admin). */
export const canManageEventBudget = (s: StaffSession) => hasGrant(s.grants, 'events.budget');
export const canManageGiveaways = (s: StaffSession) => hasGrant(s.grants, 'giveaways.manage');
export const canManageTournaments = (s: StaffSession) => hasGrant(s.grants, 'tournaments.manage');
export const canManageContacts = (s: StaffSession) => hasGrant(s.grants, 'contacts.manage');
/** Contact messages with the subject Direção (F-018): only the Direção role. */
export const canReadDirectionContacts = (s: StaffSession) => hasGrant(s.grants, 'contacts.direcao');
export const hasContactsAccess = (s: StaffSession) =>
  canManageContacts(s) || canReadDirectionContacts(s);

export const canManageStreaming = (s: StaffSession) => hasGrant(s.grants, 'streaming.manage');
/** Padrinho/Madrinha (GS1): only their own afilhados, inside the Streaming section. */
export const isStreamingPadrinho = (s: StaffSession) => hasGrant(s.grants, 'streaming.padrinho');
/** Programador do canal (GS16): the Grow uP channel grid and the streamers' requests. */
export const canProgramChannel = (s: StaffSession) => hasGrant(s.grants, 'streaming.channel');
export const hasStreamingAccess = (s: StaffSession) =>
  canManageStreaming(s) || isStreamingPadrinho(s) || canProgramChannel(s);

export const canManageContent = (s: StaffSession) => hasGrant(s.grants, 'content.manage');
export const canManagePartners = (s: StaffSession) => hasGrant(s.grants, 'partners.manage');
/** Teams: the Teams Manager runs all of them, a team Manager only their own. */
export const canManageTeams = (s: StaffSession) => hasGrant(s.grants, 'teams.manage');
export const hasTeamsAccess = (s: StaffSession) =>
  canManageTeams(s) || hasGrant(s.grants, 'teams.own');
/** Social media planning: the Social Media Manager runs it, Influencers work in their plans. */
export const canManageSocial = (s: StaffSession) => hasGrant(s.grants, 'social.manage');
export const hasSocialAccess = (s: StaffSession) =>
  canManageSocial(s) || hasGrant(s.grants, 'social.create');
